// src/screens/DashboardScreen.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Collapse,
  LinearProgress,
  Fab,
  List,
  ListItem,
  Divider,
  Chip,
  IconButton,
  Switch
} from '@mui/material';
import {
  Add,
  TrendingUp,
  Paid,
  Schedule,
  Info,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { 
  getAllDeliveries, 
  getMonthlyTarget, 
  getAllRestDays,
} from '../services/dataService';
import { DeliveryRecord, RestDay, calculateActualWorkingMinutes } from '../types';
import AddDeliveryDialog from '../components/AddDeliveryDialog';

interface Props {
  onShowSnackbar: (message: string, severity?: 'success' | 'error') => void;
  simpleMode?: boolean;
  onSimpleModeChange?: (checked: boolean) => void;
}

type NotificationItem = {
  title: string;
  message: string;
  date: string;
  severity: '新機能' | 'warning' | '修正アップデート';
};

const getMonthPrefix = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

function DashboardScreen({ onShowSnackbar, simpleMode = false, onSimpleModeChange }: Props) {
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);
  const [monthlyTarget, setMonthlyTarget] = useState(150000);
  const [restDays, setRestDays] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  // 手動でお知らせ内容を定義(必要に応じてここを編集)
  const notifications: NotificationItem[] = [
    {
      title: '分析ページとシンプルモードを改善しました',
      message: '分析ページを見やすく整理しました。売上・利益・時給・件数・サービス別の表示を切り替えながら、期間ごとの状況を確認しやすくしています。\n\n新しくシンプルモードを追加しました。売上管理を中心に使いたい場合は、時給や稼働時間まわりの表示・入力を減らして、記録画面もシンプルに使えます。\n\n休日設定では、月をまたいだときに先月の休みが今月の稼働日計算へ混ざる問題を修正しました。アップデート後の初回起動時には、最新情報のポップアップでも変更点を確認できます。',
      date: '2026-06-03',
      severity: '新機能' as const
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [deliveriesData, target, restDaysData] = await Promise.all([
        getAllDeliveries(),
        getMonthlyTarget(),
        getAllRestDays(),
      ]);
      const currentMonthPrefix = getMonthPrefix(new Date());
      setDeliveries(deliveriesData);
      setMonthlyTarget(target);
      setRestDays(restDaysData
        .map((d: RestDay) => d.date)
        .filter((date) => date.startsWith(currentMonthPrefix)));
    } catch (error) {
      console.error('データ読み込みエラー:', error);
      onShowSnackbar('データの読み込みに失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 今日のデータ (複数プラットフォーム対応)
  const getTodayData = () => {
    const today = new Date().toISOString().split('T')[0];
    const todayDeliveries = deliveries.filter(d => d.date === today);
    
    const revenue = todayDeliveries.reduce((sum, d) => sum + d.totalRevenue, 0);
    const count = todayDeliveries.reduce((sum, d) => sum + d.deliveryCount, 0);
    
    // 入力済みのプラットフォーム稼働時間を優先して実働時間を計算
    const minutes = calculateActualWorkingMinutes(todayDeliveries);
    
    return { revenue, count, minutes, deliveries: todayDeliveries };
  };

  // 今日のサービス別集計
  const getTodayPlatformData = () => {
    const today = new Date().toISOString().split('T')[0];
    const todayDeliveries = deliveries.filter(d => d.date === today);
    
    // プラットフォーム別の統計
    const platformStats: { [key: string]: { revenue: number; count: number; minutes: number } } = {};
    
    todayDeliveries.forEach(d => {
      if (!platformStats[d.platform]) {
        platformStats[d.platform] = { revenue: 0, count: 0, minutes: 0 };
      }
      platformStats[d.platform].revenue += d.totalRevenue;
      platformStats[d.platform].count += d.deliveryCount;
      platformStats[d.platform].minutes += calculateActualWorkingMinutes([d]);
    });
    
    const totalRevenue = Object.values(platformStats).reduce((sum, p) => sum + p.revenue, 0);
    
    const result = Object.entries(platformStats)
      .map(([platform, stats]) => ({
        platform,
        ...stats,
        percentage: totalRevenue > 0 ? Math.round((stats.revenue / totalRevenue) * 100) : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);
    
    return result;
  };

  const todayData = getTodayData();
  const todayPlatformData = getTodayPlatformData();
  const now = new Date();
  const daysInCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const workingDays = Math.max(1, daysInCurrentMonth - restDays.length);
  const dailyTarget = Math.floor(monthlyTarget / workingDays);
  const todayProgress = dailyTarget > 0 ? Math.min(100, Math.floor((todayData.revenue / dailyTarget) * 100)) : 0;
  
  const avgHourlyRate = todayData.minutes > 0 ? Math.floor((todayData.revenue / todayData.minutes) * 60) : 0;
  const avgPerDelivery = todayData.count > 0 ? Math.floor(todayData.revenue / todayData.count) : 0;

  const formatCurrency = (value: number) => {
    return `¥${value.toLocaleString()}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#0a0a0a' }}>
        <LinearProgress sx={{ width: '80%', bgcolor: '#1a4032', '& .MuiLinearProgress-bar': { bgcolor: '#10B981' } }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, pb: 11, bgcolor: '#0b0f0d', minHeight: '100vh' }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography fontSize={14} color="#7f8b85" sx={{ mb: 0.5 }}>
          {'\u4eca\u65e5\u306e\u7a3c\u50cd\u30b5\u30de\u30ea\u30fc'}
        </Typography>
        <Typography variant="h5" fontWeight={800} color="white">
          {'\u58f2\u4e0a\u3068\u76ee\u6a19\u3092\u30b5\u30c3\u3068\u78ba\u8a8d'}
        </Typography>
      </Box>

      <Card sx={{ mb: 1.5, bgcolor: simpleMode ? '#102118' : '#151a17', borderRadius: '14px', border: simpleMode ? '1px solid rgba(16,185,129,0.28)' : '1px solid rgba(255,255,255,0.08)' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontSize={16} fontWeight={800} color="white">
                シンプルモード
              </Typography>
              <Typography fontSize={13} color="#9ca7a0" lineHeight={1.45}>
                ONにすると記録追加で時間入力を閉じた状態にします。売上と件数だけでも保存できます。
              </Typography>
            </Box>
            <Switch
              checked={simpleMode}
              onChange={(e) => onSimpleModeChange?.(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#10B981'
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  bgcolor: '#10B981'
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 1.5, bgcolor: '#151a17', borderRadius: '14px', border: '1px solid rgba(245,158,11,0.35)' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: showNotifications ? 2 : 1.25 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: showNotifications ? 1.5 : 0
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Info sx={{ fontSize: 20, color: '#F59E0B', flexShrink: 0 }} />
              <Typography variant="h6" fontWeight="bold" color="#F59E0B" noWrap>
                {'\u304A\u77E5\u3089\u305B'}
              </Typography>
              {notifications.length > 0 && (
                <Chip
                  label={`${notifications.length}\u4EF6`}
                  size="small"
                  sx={{ bgcolor: '#2a1a0a', color: '#F59E0B', fontSize: 11 }}
                />
              )}
            </Box>
            <IconButton
              size="small"
              aria-label={showNotifications ? '\u304A\u77E5\u3089\u305B\u3092\u9589\u3058\u308B' : '\u304A\u77E5\u3089\u305B\u3092\u958B\u304F'}
              onClick={() => setShowNotifications((value) => !value)}
              sx={{ color: '#F59E0B', flexShrink: 0 }}
            >
              {showNotifications ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={showNotifications} timeout="auto" unmountOnExit>
            {notifications.length === 0 ? (
              <Typography fontSize={16} color="#888">
                {'\u73FE\u5728\u304A\u77E5\u3089\u305B\u306F\u3042\u308A\u307E\u305B\u3093'}
              </Typography>
            ) : (
              <List sx={{ p: 0 }}>
                {notifications.map((notif, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        px: 0,
                        py: 1,
                        alignItems: 'flex-start',
                        position: 'relative',
                        overflow: 'hidden',
                        minHeight: { xs: 300, sm: 340 }
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          right: { xs: -14, sm: 6 },
                          top: 0,
                          bottom: 0,
                          width: { xs: 152, sm: 210 },
                          background: 'linear-gradient(90deg, rgba(21,26,23,0), rgba(16,185,129,0.10) 46%, rgba(245,158,11,0.08))',
                          pointerEvents: 'none',
                          zIndex: 0
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '72%', sm: '68%' }, position: 'relative', zIndex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 0.5, gap: 1 }}>
                          <Typography fontSize={16} fontWeight="bold" color="white">
                            {notif.title}
                          </Typography>
                          <Chip
                            label={notif.severity === 'warning' ? '\u91CD\u8981' : '\u60C5\u5831'}
                            size="small"
                            color={notif.severity === 'warning' ? 'warning' : 'default'}
                            sx={{ alignSelf: 'flex-start', fontSize: 11, flexShrink: 0 }}
                          />
                        </Box>
                        <Typography fontSize={15} color="#ccc" sx={{ mb: 0.5, whiteSpace: 'pre-wrap' }}>
                          {notif.message}
                        </Typography>
                        <Typography fontSize={13} color="#666">
                          {notif.date}
                        </Typography>
                      </Box>
                      <Box
                        component="img"
                        src="/assets/announcement-character-transparent.png"
                        alt=""
                        sx={{
                          position: 'absolute',
                          right: { xs: -48, sm: -24 },
                          bottom: { xs: -30, sm: -40 },
                          width: { xs: 232, sm: 312 },
                          height: { xs: 330, sm: 414 },
                          objectFit: 'contain',
                          objectPosition: 'bottom center',
                          opacity: 0.96,
                          filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.42))',
                          pointerEvents: 'none'
                        }}
                      />
                    </ListItem>
                    {index < notifications.length - 1 && <Divider sx={{ my: 0.5 }} />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Collapse>
        </CardContent>
      </Card>

      {/* 今日の売上カード */}
      <Card
        sx={{
          mb: 1.5,
          bgcolor: '#132019',
          borderRadius: '18px',
          border: '1px solid rgba(16,185,129,0.22)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(59,130,246,0.04) 45%, rgba(245,158,11,0.05))',
            pointerEvents: 'none'
          }
        }}
      >
        <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1, '&:last-child': { pb: 2.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            <Box>
              <Typography fontSize={15} color="#8fb9a8" sx={{ mb: 0.5 }}>
                今日の売上
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="white" sx={{ lineHeight: 1.05 }}>
                {formatCurrency(todayData.revenue)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Card sx={{
                display: 'inline-block',
                bgcolor: todayProgress >= 100 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                borderRadius: '8px',
                mb: 0.5
              }}>
                <Typography
                  fontSize={20}
                  fontWeight="bold"
                  color={todayProgress >= 100 ? '#10B981' : '#F59E0B'}
                  sx={{ px: 1.5, py: 0.75 }}
                >
                  {todayProgress}%
                </Typography>
              </Card>
              <Typography fontSize={15} color="#9aa7a1">
                {todayData.count}件
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={todayProgress}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: '#1a4032',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#10B981',
                  borderRadius: 5
                }
              }}
            />
            <Typography fontSize={14} color="#7f8b85" sx={{ mt: 1 }}>
              日割り目標: {formatCurrency(dailyTarget)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* 統計カード */}
      <Box sx={{ display: 'grid', gridTemplateColumns: simpleMode ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 1, mb: 1.5 }}>
        {!simpleMode && (
          <Card sx={{ bgcolor: '#151a17', borderRadius: '14px' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <TrendingUp sx={{ fontSize: 20, color: '#8B5CF6', mb: 1 }} />
              <Typography fontSize={18} fontWeight="bold" color="white" sx={{ mb: 0.5 }}>
                {formatCurrency(avgHourlyRate)}
              </Typography>
              <Typography fontSize={14} color="#93a09a">平均時給</Typography>
            </CardContent>
          </Card>
        )}
        <Card sx={{ bgcolor: '#151a17', borderRadius: '14px' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Paid sx={{ fontSize: 20, color: '#3B82F6', mb: 1 }} />
            <Typography fontSize={18} fontWeight="bold" color="white" sx={{ mb: 0.5 }}>
              {formatCurrency(avgPerDelivery)}
            </Typography>
            <Typography fontSize={14} color="#93a09a">平均単価</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* 今日のサービス別集計 */}
      <Card sx={{ mb: 1.5, bgcolor: '#151a17', borderRadius: '16px' }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Typography variant="h6" fontWeight="bold" color="white" sx={{ mb: 2 }}>
            📊 今日のサービス別集計
          </Typography>
          
          {todayPlatformData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: '#888' }}>
              <Typography fontSize={16}>今日のデータがありません</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {todayPlatformData.map((item, index) => (
                <Box key={item.platform}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: index === 0 ? '#10B981' : index === 1 ? '#3B82F6' : '#8B5CF6',
                        }}
                      />
                      <Typography fontSize={16} fontWeight="bold" color="white">
                        {item.platform}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography fontSize={17} fontWeight="bold" color="#10B981">
                        {formatCurrency(item.revenue)}
                      </Typography>
                      <Typography fontSize={13} color="#888">
                        {item.count}件
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={item.percentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: '#2a2a2a',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: index === 0 ? '#10B981' : index === 1 ? '#3B82F6' : '#8B5CF6',
                            borderRadius: 4
                          }
                        }}
                      />
                    </Box>
                    <Typography fontSize={14} color="#888" sx={{ minWidth: 45, textAlign: 'right' }}>
                      {item.percentage}%
                    </Typography>
                  </Box>

                  {!simpleMode && item.minutes > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <Schedule sx={{ fontSize: 14, color: '#666' }} />
                      <Typography fontSize={13} color="#666">
                        {Math.floor(item.minutes / 60)}h {item.minutes % 60}m
                      </Typography>
                      <Typography fontSize={13} color="#666" sx={{ ml: 1 }}>
                        時給: {item.minutes > 0 ? formatCurrency(Math.floor((item.revenue / item.minutes) * 60)) : '-'}
                      </Typography>
                    </Box>
                  )}

                  {index < todayPlatformData.length - 1 && (
                    <Divider sx={{ mt: 1.5, borderColor: '#2a2a2a' }} />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* FAB */}
      <Fab
        variant="extended"
        onClick={() => setShowAddDialog(true)}
        sx={{
          position: 'fixed',
          bottom: 88,
          right: 24,
          height: 56,
          px: 2.5,
          bgcolor: '#10B981',
          color: '#07120d',
          fontWeight: 800,
          boxShadow: '0 14px 30px rgba(16,185,129,0.28)',
          '&:hover': { bgcolor: '#34d399' }
        }}
      >
        <Add sx={{ fontSize: 24, mr: 0.75 }} />
        {'記録'}
      </Fab>

      {/* ダイアログ */}
      <AddDeliveryDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSuccess={loadData}
        onShowSnackbar={onShowSnackbar}
        simpleMode={simpleMode}
      />
    </Box>
  );
}

export default DashboardScreen;

